import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../App';
import { usePortalTheme } from '../../context/PortalThemeContext';
import {
  LayoutDashboard, GraduationCap, CalendarDays, Sparkles, Users, UserCog, Compass,
  Settings, LogOut, ChevronDown, ExternalLink, Menu, X, Sun, Moon, Video, Radio, BookOpen, LayoutGrid, ClipboardList, FileText, KeyRound, MoreHorizontal,
} from 'lucide-react';
import { ChangePasswordDialog } from '../../components/ChangePasswordDialog';
import { useToolAccess } from '../../components/portal/ToolGate';
import { PortalDesktopNav, PortalMobileNav } from '../../components/portal/PortalNav';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_736b5617-324a-4027-a0b5-949393e5cf90/artifacts/te4le6nd_Untitled%20design.png";

const buildNav = ({ isCoach, locked, studioLocked }) => [
  { key: 'dashboard', label: 'Dashboard', to: '/portal', icon: LayoutDashboard },
  { key: 'book', label: 'Book a Session', to: '/portal/book', icon: CalendarDays },
  { key: 'sessions', label: 'My Sessions', to: '/portal/sessions', icon: Video },
  { key: 'clarity-group', label: 'Clarity Hub', icon: Compass, children: [
    { key: 'clarity', label: 'Clarity Hub', to: '/portal/clarity-hub', icon: Compass, desc: 'Jumpstart, Nonprofit & Website Vision tools', locked: locked(['clarity_hub', 'jumpstart', 'nonprofit', 'website_vision']) },
    { key: 'blueprint', label: 'My Blueprint', to: '/portal/my-blueprint', icon: FileText, desc: 'Your strategy roadmap from David', locked: locked(['blueprint']) },
  ] },
  { key: 'learn', label: 'Learn', icon: BookOpen, children: [
    { key: 'programs', label: 'Programs', to: '/portal/programs', icon: GraduationCap, desc: 'Coaching offers & packages' },
    { key: 'training', label: 'Live Training', to: '/portal/training', icon: Radio, desc: 'Workshops & webinars', locked: locked(['live_training']) },
    { key: 'resources', label: 'Resources', to: '/portal/resources', icon: BookOpen, desc: 'Guides, templates & downloads' },
  ] },
  { key: 'studio', label: 'Social Studio', to: '/portal/studio/intelligence', icon: Sparkles, locked: studioLocked },
  { key: 'more', label: 'More', icon: MoreHorizontal, children: [
    ...(isCoach ? [{ key: 'jumpstart', label: 'Jumpstart Sessions', to: '/portal/jumpstart-sessions', icon: ClipboardList, desc: 'Your coaching sessions' }] : []),
    { key: 'affiliates', label: 'Affiliates', to: '/portal/affiliates', icon: Users, desc: 'Refer & earn', soon: true },
    { key: 'coaches', label: 'Coaches', to: '/portal/coaches', icon: UserCog, desc: 'Meet the coaching team', soon: true },
  ] },
];

/**
 * Self-contained member portal shell. Light by default, switchable to dark.
 * Sticky branded header with logo, in-portal nav, theme toggle, and a profile
 * dropdown (Settings / Account / Back to site / Logout). All colors are driven
 * by CSS variables (.portal-root tokens) so the theme swaps instantly.
 */
export default function PortalLayout({ children }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = usePortalTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const handleLogout = async () => { await logout(); navigate('/login'); };
  const initials = (user?.name || 'U').split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase();
  const isCoach = user?.is_coach === true || user?.role === 'coach';
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const access = useToolAccess();
  const navItems = buildNav({
    isCoach,
    locked: (tools) => !access.loading && !tools.some((t) => access.has(t)),
    studioLocked: !isAdmin && user?.is_studio_approved !== true,
  });

  return (
    <div className="portal-root min-h-screen" data-portal-theme={theme} style={{ fontFamily: 'Manrope, sans-serif' }}>
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl border-b" style={{ background: 'var(--p-headerbg)', borderColor: 'var(--p-border)' }}>
        <div className="max-w-[1700px] mx-auto px-5 sm:px-8 lg:px-12">
          <div className="flex items-center justify-between h-20 gap-4">
            <div className="flex items-center gap-3 shrink-0">
              <button className="lg:hidden" style={{ color: 'var(--p-muted)' }} onClick={() => setMobileNav((v) => !v)} aria-label="Toggle menu" data-testid="portal-mobile-menu">
                {mobileNav ? <X size={22} /> : <Menu size={22} />}
              </button>
              <Link to="/portal" className="flex items-center gap-3">
                <img src={LOGO_URL} alt="DCA Enterprises" className="h-12 sm:h-16 w-auto" />
                <span className="text-lg font-bold hidden sm:inline leading-tight" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--p-text)' }}>Member Portal</span>
              </Link>
            </div>

            <PortalDesktopNav items={navItems} />

            <div className="flex items-center gap-1.5 shrink-0">
              {(user?.role === 'admin' || user?.role === 'super_admin') && (
                <Link to="/choose-workspace" data-testid="portal-switch-workspace"
                  className="hidden sm:inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full transition-colors"
                  style={{ color: 'var(--p-text)', background: 'var(--p-hover)' }}
                  title="Switch between Member Portal and Admin Console">
                  <LayoutGrid size={14} /> Switch workspace
                </Link>
              )}
              {/* Theme toggle */}
              <button onClick={toggleTheme} aria-label="Toggle theme" data-testid="portal-theme-toggle"
                className="p-2 rounded-lg transition-colors" style={{ color: 'var(--p-muted)' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--p-hover)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
              </button>

              {/* Profile dropdown */}
              <div className="relative" ref={menuRef}>
                <button onClick={() => setMenuOpen((v) => !v)} data-testid="profile-dropdown"
                  className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-full transition-colors"
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--p-hover)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                  <span className="w-8 h-8 rounded-full bg-gradient-to-br from-[#1D4ED8] to-[#F97316] flex items-center justify-center text-xs font-bold text-white">
                    {initials}
                  </span>
                  <span className="hidden sm:inline text-sm font-medium max-w-[120px] truncate" style={{ color: 'var(--p-text)' }}>{user?.name?.split(' ')[0]}</span>
                  <ChevronDown size={15} className={`transition-transform ${menuOpen ? 'rotate-180' : ''}`} style={{ color: 'var(--p-faint)' }} aria-hidden="true" />
                </button>

                {menuOpen && (
                  <div className="absolute right-0 mt-2 w-60 rounded-xl shadow-2xl overflow-hidden border" style={{ background: 'var(--p-surface)', borderColor: 'var(--p-border)' }} data-testid="profile-menu">
                    <div className="px-4 py-3 border-b" style={{ borderColor: 'var(--p-border)' }}>
                      <div className="text-sm font-semibold truncate" style={{ color: 'var(--p-text)' }}>{user?.name}</div>
                      <div className="text-xs truncate" style={{ color: 'var(--p-faint)' }}>{user?.email}</div>
                    </div>
                    <div className="py-1">
                      {[
                        { to: '/portal/settings', icon: Settings, label: 'Settings', testid: 'profile-menu-settings' },
                        { to: '/portal/settings', icon: UserCog, label: 'Account & security', testid: 'profile-menu-account' },
                      ].map((it) => (
                        <Link key={it.label} to={it.to} onClick={() => setMenuOpen(false)} data-testid={it.testid}
                          className="flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors" style={{ color: 'var(--p-muted)' }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--p-hover)'; e.currentTarget.style.color = 'var(--p-text)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--p-muted)'; }}>
                          <it.icon size={15} aria-hidden="true" /> {it.label}
                        </Link>
                      ))}
                      <button onClick={() => { setMenuOpen(false); setPwOpen(true); }} data-testid="profile-menu-change-password"
                        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors" style={{ color: 'var(--p-muted)' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--p-hover)'; e.currentTarget.style.color = 'var(--p-text)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--p-muted)'; }}>
                        <KeyRound size={15} aria-hidden="true" /> Change password
                      </button>
                      <button onClick={toggleTheme} data-testid="profile-menu-theme"
                        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors" style={{ color: 'var(--p-muted)' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--p-hover)'; e.currentTarget.style.color = 'var(--p-text)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--p-muted)'; }}>
                        {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />} {theme === 'dark' ? 'Light mode' : 'Dark mode'}
                      </button>
                      <a href="/" className="flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors" style={{ color: 'var(--p-muted)' }} data-testid="profile-menu-site"
                        onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--p-hover)'; e.currentTarget.style.color = 'var(--p-text)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--p-muted)'; }}>
                        <ExternalLink size={15} aria-hidden="true" /> Back to main site
                      </a>
                    </div>
                    <div className="py-1 border-t" style={{ borderColor: 'var(--p-border)' }}>
                      <button onClick={handleLogout} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-500 hover:bg-red-500/10 transition-colors" data-testid="profile-menu-logout">
                        <LogOut size={15} aria-hidden="true" /> Log out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {mobileNav && <PortalMobileNav items={navItems} onNavigate={() => setMobileNav(false)} />}
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="portal-main">
        {children}
      </main>
    </div>
  );
}

